import {Component, OnInit, Optional, ChangeDetectionStrategy} from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import {FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {Offer} from '../../../../../models/offer';
import {LoaderService} from '../../../../../services/loader/loader.service';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';
import {OfferService} from '../../../../../services/offer/offer.service';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {InputSwitchComponent} from '../../../../layout/input-switch/input-switch.component';
import {ProductService} from '../../../../../services/product/product.service';
import {forkJoin} from 'rxjs';
import {Product} from '../../../../../models/product';
import {Country} from '../../../../../models/country';
import {CurrencyService} from '../../../../../services/currency/currency.service';
import {Currency} from '../../../../../models/currency';
import {DropdownSelectComponent} from '../../../../layout/dropdown-select/dropdown-select.component';
import {
  DropdownSelectOptionComponent
} from '../../../../layout/dropdown-select-option/dropdown-select-option.component';
import {CountryService} from '../../../../../services/country/country.service';
import {OfferTabComponent} from '../offer-tab/offer-tab.component';
import {IsGranted} from '../../../../../decorator/is-granted';
import {AuthService} from '../../../../../services/auth/auth.service';
import {MetaTitle} from '../../../../../decorator/meta-title';
import {TranslatePipe} from '@ngx-translate/core';
import {ConstantList} from '../../../../../models/constant-list';
import {geoGroups} from '../../../../../constants/geo-groups';
import {
  OfferGoalSetupComponent,
  OfferGoalSetupForm,
  toOfferGoalsPayload
} from '../offer-goal-setup/offer-goal-setup.component';

@Component({
  selector: 'app-offer-form',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    InputSwitchComponent,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    NgOptimizedImage,
    TranslatePipe,
    FormsModule,
    OfferGoalSetupComponent
],
  templateUrl: './offer-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './offer-form.component.scss'
})
@IsGranted(['ROLE_API_OFFER_READ'])
@MetaTitle('Offer form')
export class OfferFormComponent implements OnInit {
  loaded = false;
  id: number | null = null;
  offer: Offer | null = null;
  products: Product[] = [];
  currencies: Currency[] = [];
  countries: Country[] = [];
  verticalList: ConstantList[] = [];
  conversionTypeList: string[] = [];
  partnerAccountTypeList: string[] = [];
  geoGroups: string[] | null = null;
  submitted = false;
  goals = new FormArray<OfferGoalSetupForm>([]);

  form = new FormGroup({
    productId: new FormControl<number | null>(null, Validators.required),
    name: new FormControl('', Validators.required),
    conversionType: new FormControl<string | null>(null),
    targetAccountType: new FormControl<string | null>(null),
    descriptionHtml: new FormControl(''),
    descriptionText: new FormControl(''),
    previewUrl: new FormControl(''),
    defaultUrl: new FormControl(''),
    currencyId: new FormControl<number | null>(null),
    countries: new FormControl<string[]>([]),
    verticals: new FormControl<number[]>([]),
    private: new FormControl(false),
    requireApproval: new FormControl(false),
    enabled: new FormControl(true),
    blockTraffic: new FormControl(false),
  });

  constructor(
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
    private offerService: OfferService,
    private productService: ProductService,
    private currencyService: CurrencyService,
    protected countryService: CountryService,
    @Optional() private offerTab: OfferTabComponent
  ) {
  }

  ngOnInit(): void {
    this.id = Number(this.route.snapshot.paramMap.get('id'));

    this.loader.startLoading();
    forkJoin([
      this.productService.findAll({sort: 'name'}),
      this.currencyService.findAllAvailable(),
      this.countryService.findAll(),
      this.auth.getAsyncConfig(),
    ]).subscribe(([products, currencies, countries, config]) => {
      this.products = products;
      this.currencies = currencies;
      this.countries = countries;
      this.verticalList = config?.data?.verticalList.map((vertical: any) => new ConstantList().deserialize(vertical));
      this.conversionTypeList = config?.data?.conversionTypeList ?? [];
      this.partnerAccountTypeList = config?.data?.partnerAccountTypeList ?? [];

      if (this.id) {
        this.offerService.findOneById(this.id, {groups: 'product, currency'}).subscribe((offer: Offer) => {
          this.offer = offer;
          this.form.patchValue({
            productId: this.offer.product?.id,
            name: this.offer.name,
            conversionType: this.offer.conversionType,
            targetAccountType: this.offer.targetAccountType,
            descriptionHtml: this.offer.descriptionHtml,
            descriptionText: this.offer.descriptionText,
            previewUrl: this.offer.previewUrl,
            defaultUrl: this.offer.defaultUrl,
            currencyId: this.offer.currency?.id,
            countries: this.offer.countries ?? [],
            verticals: this.offer.verticals ?? [],
            private: this.offer.private,
            requireApproval: this.offer.requireApproval,
            blockTraffic: this.offer.blockTraffic,
            enabled: this.offer.enabled,
          });
          this.loader.stopLoading();
          this.loaded = true;
        });
      } else {
        this.loader.stopLoading();
        this.loaded = true;
      }
    });
  }

  onSubmit(): void {
    this.submitted = true;

    if (!this.form.valid) {
      return;
    }

    if (this.isCreate() && this.isAffiliate() && !this.goals.length) {
      this.snackbar.error('Add at least one goal before creating the offer.');
      return;
    }

    this.loader.startLoading();

    let params: any = this.form.value;

    if (this.isCreate()) {
      params = {...params, goals: toOfferGoalsPayload(this.goals)};
      this.offerService.create(params).subscribe((response) => {
        this.loader.stopLoading();
        this.snackbar.success('Offer has been created.');
        this.router.navigateByUrl('/configuration/offers/' + response.id + '/goals').then();
      });
    } else if (this.offer?.id) {
      this.offerService.update(this.offer.id, params).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('Offer has been modified.');
        this.offerTab.refresh();
        this.router.navigateByUrl('/configuration/offers/' + this.offer?.id).then();
      });
    }
  }

  isCreate(): boolean {
    return !this.id;
  }

  canUpdate(): boolean {
    return this.auth.hasRolesSync(['ROLE_API_OFFER_WRITE']);
  }

  isAffiliate(): boolean {
    return !this.form.value.targetAccountType || this.form.value.targetAccountType === 'affiliate';
  }

  getGeoGroups(): {label: string, value: string[]}[] {
    return geoGroups;
  }

  selectGeoGroup(): void {
    this.form.get('countries')?.patchValue(this.geoGroups ?? []);
    this.form.updateValueAndValidity();
  }

  resetGeoGroups(): void {
    this.geoGroups = null;
  }
}
